/**
 * Regras da tela de parâmetros (AF-4126). Puras e fora do serviço HTTP: os testes da tela mockam o
 * serviço e precisam rodar estas regras de verdade. O billing confere as mesmas regras ao gravar.
 */

export const NATURES = ['INSTALLMENT', 'RECEIVABLE', 'GUARANTEE'] as const;
export type Nature = typeof NATURES[number];

export interface PaymentSource {
    alias: string;
    name: string;
    can_guarantee_refund: boolean;
    default_if: string[];
}

export type ResidualMode = 'NONE' | 'MANUAL' | 'AUTO';

export interface ResidualControls {
    mode: ResidualMode;
    activeCollection: boolean;
    boleto: boolean;
    hidden: boolean;
}

/** Os valores de `residual` aceitos por `billing/docs/specs/invoice-payment-action.schema.json`. */
export const RESIDUAL_VALUES = [
    'NONE', 'MANUAL', 'AUTO', 'AUTO_UNCOLLECT', 'AUTO_BOLETO', 'AUTO_UNCOLLECT_BOLETO', 'AUTO_HIDDEN',
    'AUTO_UNCOLLECT_HIDDEN', 'AUTO_BOLETO_HIDDEN', 'AUTO_UNCOLLECT_BOLETO_HIDDEN',
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ASSIGNOR_PLACEHOLDER = '{{assignor_name}}';

export const isUuid = (value: string): boolean => UUID_PATTERN.test(value.trim());

export const hasAssignorPlaceholder = (value: string): boolean => value.includes(ASSIGNOR_PLACEHOLDER);

const withNature = (defaultIf: string[], nature: Nature): string[] =>
    NATURES.filter(item => item === nature || defaultIf.includes(item));

/** Marcar uma natureza num bloco desmarca a mesma natureza nos outros: só uma fonte é a padrão. */
export const toggleDefaultIf = (
    sources: PaymentSource[], alias: string, nature: Nature, checked: boolean,
): PaymentSource[] => sources.map(source => {
    if (source.alias !== alias) {
        return checked ? { ...source, default_if: source.default_if.filter(item => item !== nature) } : source;
    }
    return {
        ...source,
        default_if: checked ? withNature(source.default_if, nature) : source.default_if.filter(item => item !== nature),
    };
});

export const missingNatures = (sources: PaymentSource[]): Nature[] =>
    NATURES.filter(nature => !sources.some(source => source.default_if.includes(nature)));

/** `AUTO` + `_UNCOLLECT` (sem cobrança ativa) + `_BOLETO` + `_HIDDEN`, na ordem do schema. */
export const composeResidual = ({ mode, activeCollection, boleto, hidden }: ResidualControls): string => {
    if (mode !== 'AUTO') return mode;
    return ['AUTO', !activeCollection && 'UNCOLLECT', boleto && 'BOLETO', hidden && 'HIDDEN']
        .filter(Boolean)
        .join('_');
};

export const decomposeResidual = (value: string): ResidualControls => {
    if (!value.startsWith('AUTO')) {
        return { mode: value === 'MANUAL' ? 'MANUAL' : 'NONE', activeCollection: true, boleto: false, hidden: false };
    }
    const flags = value.split('_');
    return {
        mode: 'AUTO',
        activeCollection: !flags.includes('UNCOLLECT'),
        boleto: flags.includes('BOLETO'),
        hidden: flags.includes('HIDDEN'),
    };
};

export const DEFAULT_PAYEE = 'DEFAULT_PAYEE';
export const BOLETO_RECEIVING_BANK_ACCOUNT = 'BOLETO_RECEIVING_BANK_ACCOUNT';
export const BILL_CESSION_DESCRIPTION = 'BILL_CESSION_DESCRIPTION';
export const PAYMENT_SOURCES = 'PAYMENT_SOURCES';
export const BOLETO_PAID_DATE = 'BOLETO_PAID_DATE';
export const BOLETO_PAID_ACTION = 'BOLETO_PAID_ACTION';
export const INVOICE_PAYMENT_ACTION = 'INVOICE_PAYMENT_ACTION';
export const CHARGE_INVOICES_ACTIONS = 'CHARGE_INVOICES_ACTIONS';

/** Os parâmetros que a tela edita, na ordem do select. Outras linhas da tabela ficam de fora. */
export const EDITABLE_PROPERTIES = [
    DEFAULT_PAYEE, BOLETO_RECEIVING_BANK_ACCOUNT, BILL_CESSION_DESCRIPTION, PAYMENT_SOURCES,
    BOLETO_PAID_DATE, BOLETO_PAID_ACTION, INVOICE_PAYMENT_ACTION, CHARGE_INVOICES_ACTIONS,
];

const JSON_PROPERTIES = [PAYMENT_SOURCES, INVOICE_PAYMENT_ACTION, CHARGE_INVOICES_ACTIONS];

export const parseJson = <T>(value: string, fallback: T): T => {
    try {
        return JSON.parse(value) as T;
    } catch {
        return fallback;
    }
};

/** O valor como a tela o edita: JSON compacto (o seed foi gravado com espaços e quebras de linha),
 *  para comparar o rascunho com o carregado pelo conteúdo e não pela formatação. */
export const canonicalValue = (name: string, value: string | null): string => {
    const text = value ?? '';
    if (!JSON_PROPERTIES.includes(name)) return text;
    const parsed = parseJson<unknown>(text, undefined);
    return parsed === undefined ? text : JSON.stringify(parsed);
};

const paymentSourcesError = (draft: string): string | null => {
    const sources = parseJson<PaymentSource[]>(draft, []);
    const unnamed = sources.find(source => !source.name.trim());
    if (unnamed) return `Nome obrigatório em ${unnamed.alias}`;
    const missing = missingNatures(sources);
    return missing.length ? `Sem fonte padrão: ${missing.join(', ')}` : null;
};

/** O motivo que impede salvar o rascunho, ou null. O billing confere as mesmas regras ao gravar. */
export const draftError = (name: string, draft: string): string | null => {
    if (name === DEFAULT_PAYEE) return isUuid(draft) ? null : 'UUID inválido';
    if (name === BILL_CESSION_DESCRIPTION) {
        return hasAssignorPlaceholder(draft) ? null : `O texto precisa conter ${ASSIGNOR_PLACEHOLDER}`;
    }
    if (name === BOLETO_RECEIVING_BANK_ACCOUNT) return draft ? null : 'Selecione uma conta';
    if (name === PAYMENT_SOURCES) return paymentSourcesError(draft);
    return null;
};
